import React, { useState, useRef, useEffect } from "react";
import { API_BASE } from "../apiConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faRobot,
  faUser,
  faSpinner,
  faLightbulb,
  faChartLine,
  faUsers,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import "./AskAlbert.css";
import PageHeader from "./layout/PageHeader";

const AskAlbert = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello! I'm Albert, your AI healthcare assistant. I can help you analyze your clients' data, provide insights about AIP scores, suggest care improvements, and answer questions about your practice. How can I help you today?",
      suggestions: [
        "Show me clients who need immediate attention",
        "What's the average AIP score trend?",
        "Which clients have improved the most?",
        "Give me a summary of my practice",
      ],
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasAutoSentRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const sendMessage = async (rawMessage) => {
    const userMessage = String(rawMessage || "").trim();
    if (!userMessage || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const locationId =
        routeLocation?.state?.locationId ||
        activeLocation?.id ||
        activeLocation?._id;

      const response = await fetch(`${API_BASE}/api/ask-albert`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: userMessage,
          locationId: locationId,
          conversationHistory: messages.slice(-6), // Last 6 messages for context
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.answer,
            suggestions: data.suggestions || [],
            insights: data.insights || null,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              data.message ||
              "I'm sorry, I encountered an error. Please try again.",
            isError: true,
          },
        ]);
      }
    } catch (error) {
      console.error("Ask Albert error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I'm having trouble connecting right now. Please check your connection and try again.",
          isError: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await sendMessage(input);
  };

  useEffect(() => {
    const starterQuestion = routeLocation?.state?.starterQuestion;
    const autoSend = routeLocation?.state?.autoSend;
    if (!starterQuestion) return;

    setInput(starterQuestion);
    inputRef.current?.focus();

    if (autoSend && !hasAutoSentRef.current) {
      hasAutoSentRef.current = true;
      sendMessage(starterQuestion);
      navigate(routeLocation.pathname, { replace: true, state: {} });
    }
  }, [routeLocation, navigate]);

  const formatMessage = (content) => {
    // Convert markdown-like formatting to HTML
    return content
      .split("\n")
      .map((line, i) => {
        // Bold text
        line = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
        // Bullet points
        if (line.startsWith("- ") || line.startsWith("• ")) {
          return `<li key=${i}>${line.substring(2)}</li>`;
        }
        // Numbered lists
        if (/^\d+\.\s/.test(line)) {
          return `<li key=${i}>${line.substring(line.indexOf(" ") + 1)}</li>`;
        }
        return line ? `<p key=${i}>${line}</p>` : "<br/>";
      })
      .join("");
  };

  return (
    <div className="ask-albert-container page-shell">
      <PageHeader
        title="Ask Albert"
        subtitle="Your AI-powered healthcare assistant."
        icon={<FontAwesomeIcon icon={faRobot} className="text-xl text-white" />}
        activeLocation={activeLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
      />

      {activeLocation && (
        <div className="albert-context">
          <span>
            Analyzing data for: <strong>{activeLocation.name}</strong>
          </span>
        </div>
      )}

      <div className="ask-albert-chat">
        <div className="messages-container">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`message ${message.role} ${
                message.isError ? "error" : ""
              }`}
            >
              <div className="message-avatar">
                <FontAwesomeIcon
                  icon={message.role === "assistant" ? faRobot : faUser}
                />
              </div>
              <div className="message-content">
                <div
                  className="message-text"
                  dangerouslySetInnerHTML={{
                    __html: formatMessage(message.content),
                  }}
                />

                {/* Insights Panel */}
                {message.insights && (
                  <div className="message-insights">
                    <div className="insights-header">
                      <FontAwesomeIcon icon={faLightbulb} /> Key Insights
                    </div>
                    <div className="insights-grid">
                      {message.insights.totalClients && (
                        <div className="insight-item">
                          <FontAwesomeIcon icon={faUsers} />
                          <span>
                            {message.insights.totalClients} Total Clients
                          </span>
                        </div>
                      )}
                      {message.insights.avgScore && (
                        <div className="insight-item">
                          <FontAwesomeIcon icon={faChartLine} />
                          <span>Avg AIP: {message.insights.avgScore}</span>
                        </div>
                      )}
                      {message.insights.atRisk && (
                        <div className="insight-item warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          <span>{message.insights.atRisk} At Risk</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                {message.suggestions && message.suggestions.length > 0 && (
                  <div className="message-suggestions">
                    {message.suggestions.map((suggestion, i) => (
                      <button
                        key={i}
                        className="suggestion-btn"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="message assistant loading">
              <div className="message-avatar">
                <FontAwesomeIcon icon={faRobot} />
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form className="ask-albert-input" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Albert anything about your clients..."
            disabled={loading}
          />
          <button type="submit" disabled={loading || !input.trim()}>
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPaperPlane} />
            )}
          </button>
        </form>
      </div>

      {!activeLocation && (
        <div className="no-location-warning">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>Please select a location to get personalized insights</span>
        </div>
      )}
    </div>
  );
};

export default AskAlbert;
